import { booleanAttribute, ChangeDetectionStrategy, Component, computed, effect, input, NgZone, OnDestroy, signal } from '@angular/core';

import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';

// De lijst die de rijen als directe kinderen heeft: het scroll-element zelf (checklist: #matList
// scrollt) of de eerste lijst erbinnen (virtual-scroll-viewport, expansion-list-container).
const LIST_SELECTOR = 'mat-nav-list, mat-selection-list';
const HIDE_DELAY_MS = 900;
// Zo lang na het binden van een (nieuw) scroll-element of een nieuwe lijst (laden, zoeken, sorteren)
// telt scrollen niet mee: dan zet de lijst zelf de bewaarde positie terug of springt naar boven
// (_position_scrollTo — in een setTimeout, dus net ná deze wijziging). Daarbuiten toont elke scroll
// de overlay, ook een vervolg na stilstand zonder nieuwe aanraking/klik (vinger of scrollbalk vastgehouden).
const QUIET_AFTER_CHANGE_MS = 600;
const SEPARATOR = ' - ';

// Transparant pilletje rechtsboven in een lijst met de stand tijdens het scrollen: "nr - totaal",
// waarbij nr de onderste zichtbare rij is (een rij telt mee zodra hij minstens half in beeld is —
// onderaan de lijst staat er dus "totaal - totaal"). Verschijnt bij scrollen, verdwijnt kort na
// stilstand en komt bij verder scrollen terug; bij een lijst die in z'n geheel past nooit.
//
// Twee manieren om nr te bepalen:
//   - virtual: de DOM bevat alleen de gerenderde rijen, dus uitrekenen uit scrollTop en de vaste
//     rijhoogte (itemSize, anders de gemeten hoogte van de eerste gerenderde rij);
//   - anders: alle rijen staan in de DOM maar kunnen in hoogte verschillen (uitklappanelen), dus de
//     onderste rij opzoeken via een binary search op de rij-posities.
//
// De scroll-listeners lopen buiten Angular's zone (geen change-detection per scroll-event); alleen een
// echte verandering van nr of zichtbaarheid gaat via __ngZone.run terug de zone in.
@Component({
    selector: 'app-list-position',
    templateUrl: './list-position.component.html',
    styleUrls: ['./list-position.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    host: {
        'aria-hidden': 'true',
        '[class.visible]': 'visible$$()',
    },
})
export class ListPositionComponent
    implements OnDestroy
{
    private readonly __ngZone = injectRequired(NgZone);

    input_itemSize$$ = input<number | null>(null, { alias: 'itemSize' });
    input_scrollElement$$ = input<HTMLElement | null>(null, { alias: 'scrollElement' });
    // De lijst zoals getoond (listFiltered$$): lengte = totaal, nieuwe referentie = nieuwe stilte-periode.
    input_list$$ = input<readonly unknown[]>([], { alias: 'list' });
    input_virtual$$ = input(false, { transform: booleanAttribute, alias: 'virtual' });

    private readonly __nr$$ = signal(0);
    private readonly __visible$$ = signal(false);

    private readonly __numberFormat = new Intl.NumberFormat(undefined);
    private __hideTimer: ReturnType<typeof setTimeout> | null = null;
    private __quietUntil = 0;

    total$$ = computed(() => this.input_list$$().length);
    text$$ = computed(() =>
        this.__numberFormat.format(this.__nr$$()) + SEPARATOR + this.__numberFormat.format(this.total$$()));
    visible$$ = this.__visible$$.asReadonly();

    constructor() {
        // Per (nieuw) scroll-element binden — een checklist krijgt bij elke wissel view ⇄ edit een
        // nieuw #matList-element.
        effect(onCleanup => {
            const __element = this.input_scrollElement$$();
            if (!__element) return;

            const __onScroll = () => this.__onScroll(__element);
            const __options: AddEventListenerOptions = { passive: true };

            this.__quiet_start();
            this.__ngZone.runOutsideAngular(() => __element.addEventListener('scroll', __onScroll, __options));

            onCleanup(() => {
                __element.removeEventListener('scroll', __onScroll);
                this.__hide();
            });
        });
        effect(() => {
            this.input_list$$();
            this.__quiet_start();
        });
    }

    private __bottomRowNr(element: HTMLElement): number {
        const __rows = element.matches(LIST_SELECTOR) ? element : element.querySelector<HTMLElement>(LIST_SELECTOR);
        if (!__rows) return 0;

        return this.input_virtual$$()
            ? this.__bottomRowNr_calculate(element, __rows)
            : this.__bottomRowNr_measure(element, __rows);
    }

    // Rij k (1-based) beslaat [(k-1)·h, k·h]; minstens half in beeld ⇔ k ≤ onderkant/h + ½ → afronden.
    private __bottomRowNr_calculate(element: HTMLElement, rows: HTMLElement): number {
        const __rowHeight = this.input_itemSize$$() || (rows.firstElementChild as HTMLElement | null)?.offsetHeight || 0;
        if (!__rowHeight) return 0;

        return Math.round((element.scrollTop + element.clientHeight) / __rowHeight);
    }

    // Laatste rij die minstens half in beeld is (een rij hoger dan de lijst: de bovenste helft van de
    // lijst). Rijen staan op volgorde onder elkaar, dus dat criterium is monotoon → binary search.
    private __bottomRowNr_measure(element: HTMLElement, rows: HTMLElement): number {
        const __children = rows.children;
        const __viewBottom = element.getBoundingClientRect().bottom;
        const __viewHeight = element.clientHeight;
        let __low = 0;
        let __high = __children.length - 1;
        let __found = -1;

        while (__low <= __high) {
            const __mid = (__low + __high) >> 1;
            const __rect = __children[__mid].getBoundingClientRect();

            if (__rect.top + Math.min(__rect.height, __viewHeight) / 2 <= __viewBottom) {
                __found = __mid;
                __low = __mid + 1;
            } else {
                __high = __mid - 1;
            }
        }
        return __found + 1;
    }

    private __hide(): void {
        if (this.__hideTimer) clearTimeout(this.__hideTimer);
        this.__hideTimer = null;
        if (this.__visible$$()) this.__ngZone.run(() => this.__visible$$.set(false));
    }

    private __onScroll(element: HTMLElement): void {
        if (performance.now() < this.__quietUntil) return;

        const __total = this.total$$();
        if (__total === 0 || element.scrollHeight <= element.clientHeight) return;

        const __nr = Math.min(Math.max(this.__bottomRowNr(element), 1), __total);
        if (__nr !== this.__nr$$() || !this.__visible$$()) {
            this.__ngZone.run(() => {
                this.__nr$$.set(__nr);
                this.__visible$$.set(true);
            });
        }

        if (this.__hideTimer) clearTimeout(this.__hideTimer);
        this.__hideTimer = setTimeout(() => this.__hide(), HIDE_DELAY_MS);
    }

    private __quiet_start(): void {
        this.__quietUntil = performance.now() + QUIET_AFTER_CHANGE_MS;
    }

    ngOnDestroy(): void {
        if (this.__hideTimer) clearTimeout(this.__hideTimer);
    }
}
